/* ==========================================================================
   Self sign-up review (MD: every company · company CEO: own company)
     <SignupBanner>   "3 sign-up requests waiting · Review" above the directory
     <SignupReview>   the review dialog: approve (sets designation, department, type, ID) or decline
   Backend: routes.js  GET /signup-requests, POST /signup-requests/:id/approve | /reject
   ========================================================================== */
import React, { useState } from 'react';
import { UserPlus, X, Check, TriangleAlert, Mail, Phone, CalendarDays, ChevronDown } from 'lucide-react';
import { empFetch, EMPLOYMENT_TYPES, isPrivilegedDesignation, formatDate } from './employeeModel';

export function SignupBanner({ count, onReview }) {
  if (!count) return null;
  return (
    <div className="su-banner" role="status">
      <span className="su-banner-icon"><UserPlus size={16} /></span>
      <span className="su-banner-text"><strong>{count} sign-up {count === 1 ? 'request' : 'requests'}</strong> waiting for approval</span>
      <button type="button" className="hr-btn hr-btn-primary hr-btn-sm" onClick={onReview}>Review</button>
    </div>
  );
}

function RequestCard({ r, isGroupMD, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    role: r.designation, department: r.department, employment_type: 'Probation', joining_date: r.joiningDate || '', emp_code: '',
  });
  const [reason, setReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [needsConfirm, setNeedsConfirm] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); if (k === 'role') setNeedsConfirm(false); };
  const privileged = isPrivilegedDesignation(form.role);

  const approve = async (confirmPrivileged = false) => {
    setError('');
    if (!form.role.trim()) { setError('Set the designation first.'); setOpen(true); return; }
    if (!form.department.trim()) { setError('Set the department first.'); setOpen(true); return; }
    setBusy('approve');
    try {
      const body = { ...form, role: form.role.trim(), department: form.department.trim(), confirmPrivileged };
      if (!body.emp_code.trim()) delete body.emp_code; else body.emp_code = body.emp_code.trim().toUpperCase();
      if (!body.joining_date) delete body.joining_date;
      const res = await empFetch(`/signup-requests/${r.id}/approve`, { method: 'POST', body });
      onDone(r.id, { approved: true, message: res.message, employee: res.employee });
    } catch (e) {
      if (e.body?.needsConfirm) { setNeedsConfirm(true); setOpen(true); }
      setError(e.message);
    } finally { setBusy(''); }
  };
  const reject = async () => {
    setError(''); setBusy('reject');
    try {
      const res = await empFetch(`/signup-requests/${r.id}/reject`, { method: 'POST', body: { reason } });
      onDone(r.id, { approved: false, message: res.message });
    } catch (e) { setError(e.message); } finally { setBusy(''); }
  };

  return (
    <article className="su-req">
      <div className="su-req-head">
        <div style={{ minWidth: 0 }}>
          <div className="su-req-name">{r.name}</div>
          <div className="su-req-meta">
            <span><Mail size={13} /> {r.email}</span>
            {r.phone && <span><Phone size={13} /> {r.phone}</span>}
          </div>
        </div>
        <span className="su-req-company">{r.companyName}</span>
      </div>
      <dl className="su-req-facts">
        <div><dt>Job title given</dt><dd>{r.designation || '—'}</dd></div>
        <div><dt>Department given</dt><dd>{r.department || '—'}</dd></div>
        <div><dt>Joining</dt><dd>{r.joiningDate ? formatDate(r.joiningDate) : '—'}</dd></div>
        <div><dt>Registered</dt><dd><CalendarDays size={12} /> {r.createdAt}</dd></div>
      </dl>

      {error && <div className={`em-alert ${needsConfirm ? 'is-warning' : 'is-error'}`} role="alert"><TriangleAlert size={16} /><span>{error}</span></div>}

      <button type="button" className="su-req-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <ChevronDown size={14} className={open ? 'is-open' : ''} /> {open ? 'Hide details' : 'Check details before approving'}
      </button>
      {open && (
        <div className="su-req-form">
          <label className="hr-field"><span className="hr-label">Designation</span>
            <input className="hr-input" value={form.role} onChange={set('role')} placeholder="e.g. Sales Executive" />
            {privileged && <span className="su-warn">{isGroupMD ? 'This designation gives management access (employee records, approvals).' : 'Only the group MD can approve MD, CEO, HR or Admin designations.'}</span>}
          </label>
          <label className="hr-field"><span className="hr-label">Department</span>
            <input className="hr-input" value={form.department} onChange={set('department')} placeholder="e.g. Sales" />
          </label>
          <label className="hr-field"><span className="hr-label">Employment type</span>
            <select className="hr-select" value={form.employment_type} onChange={set('employment_type')}>
              {EMPLOYMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="hr-field"><span className="hr-label">Date of joining</span>
            <input type="date" className="hr-input" value={form.joining_date} onChange={set('joining_date')} />
          </label>
          <label className="hr-field"><span className="hr-label">Employee ID</span>
            <input className="hr-input" value={form.emp_code} onChange={set('emp_code')} placeholder="Leave blank for the next ID" />
          </label>
        </div>
      )}

      {rejecting ? (
        <div className="su-req-actions">
          <input className="hr-input su-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional, not shown to them)" aria-label="Reason for declining" />
          <button type="button" className="hr-btn hr-btn-ghost hr-btn-sm" onClick={() => setRejecting(false)} disabled={!!busy}>Cancel</button>
          <button type="button" className="hr-btn hr-btn-danger-soft hr-btn-sm" onClick={reject} disabled={!!busy}>{busy === 'reject' ? 'Declining…' : 'Decline'}</button>
        </div>
      ) : (
        <div className="su-req-actions">
          <button type="button" className="hr-btn hr-btn-ghost hr-btn-sm" onClick={() => setRejecting(true)} disabled={!!busy}><X size={14} /> Decline</button>
          <span className="su-spacer" />
          {needsConfirm ? (
            <button type="button" className="hr-btn hr-btn-primary hr-btn-sm" onClick={() => approve(true)} disabled={!!busy}>
              {busy === 'approve' ? 'Approving…' : <><Check size={14} /> Approve with management access</>}
            </button>
          ) : (
            <button type="button" className="hr-btn hr-btn-primary hr-btn-sm" onClick={() => approve(false)} disabled={!!busy}>
              {busy === 'approve' ? 'Approving…' : <><Check size={14} /> Approve</>}
            </button>
          )}
        </div>
      )}
    </article>
  );
}

export function SignupReview({ requests, isGroupMD, onClose, onDone }) {
  const [flash, setFlash] = useState('');
  const done = (id, result) => { setFlash(result.message); onDone(id, result); };
  return (
    <div className="hr-modal-scrim em-scrim" role="dialog" aria-modal="true" aria-labelledby="su-review-title" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="su-review">
        <header className="em-add-head">
          <div className="em-add-head-icon"><UserPlus size={18} /></div>
          <div>
            <h2 id="su-review-title" className="em-add-title">Sign-up requests</h2>
            <p className="em-add-sub">People who registered themselves. Approving creates their account; they sign in with the password they chose.</p>
          </div>
          <button type="button" className="hr-btn hr-btn-ghost hr-btn-icon em-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </header>
        <div className="su-review-body">
          {flash && <div className="em-alert is-success" role="status"><Check size={16} /><span>{flash}</span></div>}
          {requests.length === 0
            ? <p className="su-empty">No requests waiting.</p>
            : requests.map((r) => <RequestCard key={r.id} r={r} isGroupMD={isGroupMD} onDone={done} />)}
        </div>
      </div>
    </div>
  );
}
